"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, Heart, Skull, RotateCcw, Zap, Flower } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function LArcanuSansNomPage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-slate-900/50 border border-slate-700/50 rounded-lg">
          <div className="flex items-center gap-2 text-slate-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-slate-500">/</span>
            <span className="text-sm">Marseille</span>
            <span className="text-slate-500">/</span>
            <span className="text-sm">Major Arkana</span>
            <span className="text-slate-500">/</span>
            <span className="text-sm">L'Arcane sans nom</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about/tarot/systems/marseille/major-arcana"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Major Arkana'ya Geri Dön
          </Link>
        </div>

        {/* Header with Card */}
        <div className="bg-gradient-to-r from-slate-900/40 to-black/60 rounded-xl p-8 mb-8 border border-slate-700/50">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-center">
            <div className="lg:col-span-1 flex justify-center">
              <div className="w-48 h-72 rounded-xl overflow-hidden shadow-2xl">
                <img
                  src="/images/tarot/cards/marseille-l-arcane-sans-nom.svg"
                  alt="L'Arcane sans nom Tarot Card"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.currentTarget;
                    target.style.display = 'none';
                    if (target.nextElementSibling) {
                      (target.nextElementSibling as HTMLElement).style.display = 'flex';
                    }
                  }}
                />
                <div className="hidden w-full h-full bg-gradient-to-b from-slate-900/60 to-black/80 border border-slate-600/50 flex-col items-center justify-center text-slate-300">
                  <span className="text-3xl font-bold mb-2">XIII</span>
                  <span className="text-lg font-semibold">L'ARCANE SANS NOM</span>
                  <span className="text-sm text-center mt-2">Death / Transformation</span>
                </div>
              </div>
            </div>
            <div className="lg:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-slate-700 to-black rounded-full flex items-center justify-center">
                  <span className="text-white font-bold text-lg">XIII</span>
                </div>
                <div>
                  <h1 className="text-4xl font-bold text-slate-100">L'Arcane sans nom</h1>
                  <p className="text-xl text-slate-400">Dönüşüm / Yeniden Doğuş</p>
                </div>
              </div>
              <p className="text-lg text-slate-200 leading-relaxed mb-4">
                Marseille geleneğinde geleneksel olarak isimsiz bırakılan XIII. kart. Dönüşüm,
                bitiş ve yeni başlangıçların sembolü. Fiziksel ölümden ziyade manevi ve ruhsal
                dönüşümü temsil eder.
              </p>
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 bg-slate-700/50 text-slate-300 rounded-full text-sm">Dönüşüm</span>
                <span className="px-3 py-1 bg-slate-700/50 text-slate-300 rounded-full text-sm">Bitiş</span>
                <span className="px-3 py-1 bg-slate-700/50 text-slate-300 rounded-full text-sm">Yeniden Doğuş</span>
                <span className="px-3 py-1 bg-slate-700/50 text-slate-300 rounded-full text-sm">Değişim</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card Meanings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Positive Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <RotateCcw className="h-6 w-6 text-green-400" />
              Düz Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Dönüşüm</h3>
                  <p className="text-slate-400 text-sm">Köklü değişim, evrim ve gelişim süreci.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Yeniden Doğuş</h3>
                  <p className="text-slate-400 text-sm">Yeni başlangıçlar, hayata farklı bakış açısı.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Bırakıp Gitme</h3>
                  <p className="text-slate-400 text-sm">Geçmişi geride bırakma, arınma ve temizlenme.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Manevi Gelişim</h3>
                  <p className="text-slate-400 text-sm">Ruhsal olgunlaşma ve üst seviye bilinç.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Negative Meanings */}
          <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <h2 className="text-2xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
              <Skull className="h-6 w-6 text-red-400" />
              Ters Pozisyon Anlamları
            </h2>
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Değişime Direnç</h3>
                  <p className="text-slate-400 text-sm">Değişimi kabul etmeme, sabitlik ve katılık.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Durgunluk</h3>
                  <p className="text-slate-400 text-sm">İlerleme eksikliği, tıkanıklık ve durağanlık.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Geçmişe Takılma</h3>
                  <p className="text-slate-400 text-sm">Eski alışkanlıklar, nostaljik bağlılık ve özlem.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-2 h-2 bg-red-400 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold text-slate-200 mb-1">Korku</h3>
                  <p className="text-slate-400 text-sm">Değişim korkusu, bilinmeyenden çekinme.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Symbolism */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Marseille Sembolizmi</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-12 h-12 bg-slate-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <Skull className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">İskelet</h3>
              <p className="text-slate-400 text-sm">Değişimin kaçınılmazlığı, dönüşüm ve temizlik.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-gray-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">⚒️</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Tırpan</h3>
              <p className="text-slate-400 text-sm">Hasat zamanı, biçme ve temizleme gücü.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">👑</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Kraliyet Başları</h3>
              <p className="text-slate-400 text-sm">Ölümün sosyal statü tanımaması, eşitlik.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <Flower className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Yeni Filizler</h3>
              <p className="text-slate-400 text-sm">Ölümden sonra yeni yaşam, yeniden doğuş.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">⚫⚪</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Renkler</h3>
              <p className="text-slate-400 text-sm">Siyah ve beyaz dualitesi, zıtlıkların birleşimi.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3">
                <span className="text-white text-lg">⚖️</span>
              </div>
              <h3 className="font-semibold text-slate-200 mb-2">Eşitlik</h3>
              <p className="text-slate-400 text-sm">Herkesin aynı kaderi paylaşması, evrensel gerçek.</p>
            </div>
          </div>
        </div>

        {/* Life Areas */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Yaşam Alanlarındaki Anlamlar</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Heart className="h-5 w-5 text-pink-400" />
                Aşk ve İlişkiler
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• İlişkide köklü değişim</li>
                <li>• Eski kalıpların yıkılması</li>
                <li>• Daha derin bağ kurma</li>
                <li>• Ayrılık veya dönüşüm</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Star className="h-5 w-5 text-yellow-400" />
                Kariyer ve İş
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Mesleki dönüşüm</li>
                <li>• Eski işin bitişi</li>
                <li>• Yeni kariyer yolları</li>
                <li>• Şirket değişiklikleri</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <RotateCcw className="h-5 w-5 text-green-400" />
                Kişisel Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Kişilik dönüşümü</li>
                <li>• Eski inançların yıkılması</li>
                <li>• Yeni benlik keşfi</li>
                <li>• Olgunlaşma süreci</li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-slate-100 mb-3 flex items-center gap-2">
                <Zap className="h-5 w-5 text-blue-400" />
                Manevi Gelişim
              </h3>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li>• Ruhsal uyanış</li>
                <li>• Eski ego yapılarının ölümü</li>
                <li>• Daha yüksek bilinç</li>
                <li>• İçsel arınma</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot/systems/marseille/major-arcana/le-pendu"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Önceki: Le Pendu
          </Link>

          <Link
            href="/about/tarot/systems/marseille/major-arcana/temperance"
            className="inline-flex items-center px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded-lg transition-colors"
          >
            Sonraki: Tempérance
            <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}